import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createContactFormSchema, type CreateContactFormData } from "./contact-form-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CompanyContactCombobox } from "./company-contact-combobox";

const emptyDefaults: CreateContactFormData = {
  firstName: "", lastName: "", email: "", phone: "",
  mobilePhone: "", officePhone: "", telephone: "", deskExtension: "",
  contactType: "claimant", entityType: "individual", companyId: null,
  address: "", city: "", state: "", zip: "", notes: "",
};

interface ContactFormContentProps {
  defaultValues?: Partial<CreateContactFormData>;
  onSubmit: (data: CreateContactFormData) => void;
  onCancel: () => void;
  isPending?: boolean;
  submitLabel?: string;
  /** When true the form mounts once and never resets (popup mode). */
  staticMount?: boolean;
}

export function ContactFormContent({
  defaultValues,
  onSubmit,
  onCancel,
  isPending,
  submitLabel = "Save",
  staticMount = false,
}: ContactFormContentProps) {
  const form = useForm<CreateContactFormData>({
    resolver: zodResolver(createContactFormSchema),
    defaultValues: { ...emptyDefaults, ...defaultValues },
  });

  const entityType = useWatch({ control: form.control, name: "entityType" });
  const isCompany = entityType === "company";

  // In modal mode, reset when defaultValues identity changes (dialog re-open).
  useEffect(() => {
    if (!staticMount) {
      form.reset({ ...emptyDefaults, ...defaultValues });
    }
  }, [staticMount, JSON.stringify(defaultValues)]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="space-y-4">

          {/* Entity Type — always first */}
          <FormField control={form.control} name="entityType" render={({ field }) => (
            <FormItem>
              <FormLabel>Type</FormLabel>
              <Select value={field.value ?? "individual"} onValueChange={(v) => {
                field.onChange(v);
                if (v === "company") {
                  form.setValue("lastName", "");
                  form.setValue("companyId", null);
                }
              }}>
                <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )} />

          {/* Name fields — conditional on entityType */}
          {isCompany ? (
            <FormField control={form.control} name="firstName" render={({ field }) => (
              <FormItem><FormLabel>Company Name</FormLabel><FormControl><Input placeholder="Enter company name" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
            )} />
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="firstName" render={({ field }) => (
                <FormItem><FormLabel>First Name</FormLabel><FormControl><Input placeholder="Enter first name" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="lastName" render={({ field }) => (
                <FormItem><FormLabel>Last Name</FormLabel><FormControl><Input placeholder="Enter last name" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
              )} />
            </div>
          )}

          <FormField control={form.control} name="contactType" render={({ field }) => (
            <FormItem>
              <FormLabel>Contact Type</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl><SelectTrigger><SelectValue placeholder="Select contact type" /></SelectTrigger></FormControl>
                <SelectContent>
                  <SelectItem value="claimant">Claimant</SelectItem>
                  <SelectItem value="appraiser">Appraiser</SelectItem>
                  <SelectItem value="umpire">Umpire</SelectItem>
                  <SelectItem value="field_adjuster">Field Adjuster</SelectItem>
                  <SelectItem value="desk_adjuster">Desk Adjuster</SelectItem>
                  <SelectItem value="carrier">Carrier</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="attorney">Attorney</SelectItem>
                  <SelectItem value="engineer">Engineer</SelectItem>
                  <SelectItem value="building_consultant">Building Consultant</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )} />

          {/* Company picker — only for individuals */}
          {!isCompany && (
            <FormField control={form.control} name="companyId" render={({ field }) => (
              <FormItem>
                <FormLabel>Company</FormLabel>
                <FormControl>
                  <CompanyContactCombobox
                    value={field.value}
                    onSelect={(c) => field.onChange(c?.id ?? null)}
                    allowCreate
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormField control={form.control} name="email" render={({ field }) => (
              <FormItem><FormLabel>Email</FormLabel><FormControl><Input type="email" placeholder="Enter email" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="phone" render={({ field }) => (
              <FormItem><FormLabel>Phone</FormLabel><FormControl><PhoneInput {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="mobilePhone" render={({ field }) => (
              <FormItem><FormLabel>Mobile</FormLabel><FormControl><PhoneInput {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="officePhone" render={({ field }) => (
              <FormItem><FormLabel>Office</FormLabel><FormControl><PhoneInput {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="telephone" render={({ field }) => (
              <FormItem><FormLabel>Telephone</FormLabel><FormControl><PhoneInput {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
            )} />
          </div>
          {/* Desk Extension — company contacts only, after telephone field */}
          {isCompany && (
            <FormField control={form.control} name="deskExtension" render={({ field }) => (
              <FormItem><FormLabel>Desk Extension</FormLabel><FormControl><Input placeholder="e.g. 1234" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
            )} />
          )}
          <FormField control={form.control} name="address" render={({ field }) => (
            <FormItem><FormLabel>Address</FormLabel><FormControl><Input placeholder="Enter street address" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
          )} />
          <div className="grid grid-cols-3 gap-4">
            <FormField control={form.control} name="city" render={({ field }) => (
              <FormItem><FormLabel>City</FormLabel><FormControl><Input placeholder="Enter city" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="state" render={({ field }) => (
              <FormItem><FormLabel>State</FormLabel><FormControl><Input placeholder="State" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="zip" render={({ field }) => (
              <FormItem><FormLabel>ZIP</FormLabel><FormControl><Input placeholder="ZIP Code" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
            )} />
          </div>
          <FormField control={form.control} name="notes" render={({ field }) => (
            <FormItem><FormLabel>Notes</FormLabel><FormControl><Textarea placeholder="Enter any additional notes" className="resize-none" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
          )} />
        </div>
        <div className="flex justify-end gap-3 border-t pt-4 mt-2">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isPending}>{isPending ? "Saving..." : submitLabel}</Button>
        </div>
      </form>
    </Form>
  );
}
